import { usePostHog } from "@posthog/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FunnelShell } from "./components/FunnelShell";
import { StepRenderer, preloadStepRendererChunk } from "./components/StepRenderer";
import { BottomConfirmBar } from "./components/ui/BottomConfirmBar";
import { TopBar } from "./components/ui/TopBar";
import { getImagePreloadCandidatesForSources } from "./components/imageMeta";
import { computeSectionProgress, getFlowSteps, getFunnelSections } from "./funnel/flowData";
import { replaceStepInUrl, resolveStepFromUrl } from "./funnel/resolveStepFromUrl";
import { useFunnelState } from "./funnel/useFunnelState";
import { useLivePrices } from "./funnel/useLivePrices";
import { buildFunnelImageRepository } from "./perf/funnelImageRepository";
import { startPhasedImagePreloadOnce } from "./perf/preloadImages";
import { getCheckoutImagePreloadUrls } from "./perf/checkoutImageAssets";
import {
  buildEmbeddedStepPageviewPayload,
  FLOW_STORAGE_KEY,
  resolveInitialFlowState
} from "./analytics/funnelTracking";
import { logMetaDedup } from "./analytics/metaDedupDebug";

const pageVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 32 : -32,
    opacity: 0,
    filter: "blur(6px)"
  }),
  center: {
    x: 0,
    opacity: 1,
    filter: "blur(0px)"
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -32 : 32,
    opacity: 0,
    filter: "blur(6px)"
  })
};

const pageTransition = {
  duration: 0.32,
  ease: [0.22, 1, 0.36, 1]
} as const;

type IdleDeadlineLike = {
  didTimeout: boolean;
  timeRemaining: () => number;
};

type ScheduledTaskHandle =
  | { type: "idle"; id: number }
  | { type: "timeout"; id: number };

type WindowWithStartupFlags = Window &
  typeof globalThis & {
    __VIREO_LAST_PAGEVIEW_URL__?: string;
    __VIREO_WELCOME_SHELL_CONTINUE__?: boolean;
    __VIREO_WELCOME_SHELL_CAROUSEL_TIMER__?: number;
    __VIREO_WELCOME_SHELL_PENDING_ANSWER__?: {
      answerKey: string;
      value: string;
    };
    requestIdleCallback?: (callback: (deadline: IdleDeadlineLike) => void, options?: { timeout?: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

const scheduleIdleTask = (work: () => void, timeout = 1500): ScheduledTaskHandle => {
  const idleWindow = window as WindowWithStartupFlags;

  if (typeof idleWindow.requestIdleCallback === "function") {
    return {
      type: "idle",
      id: idleWindow.requestIdleCallback(
        (deadline) => {
          if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
            work();
            return;
          }

          work();
        },
        { timeout }
      )
    };
  }

  return {
    type: "timeout",
    id: window.setTimeout(work, timeout)
  };
};

const cancelScheduledTask = (task: ScheduledTaskHandle | null): void => {
  if (!task) {
    return;
  }

  if (task.type === "idle") {
    (window as WindowWithStartupFlags).cancelIdleCallback?.(task.id);
    return;
  }

  window.clearTimeout(task.id);
};

const preloadLateStepModules = (): Promise<unknown[]> => {
  return Promise.all([
    import("./api/checkout"),
    import("./api/leads"),
    import("./funnel/buildOnboardingResponses"),
    import("./analytics/metaPixel")
  ]);
};

const readStoredFlow = (): "a" | "b" | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(FLOW_STORAGE_KEY);
    return stored === "a" || stored === "b" ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredFlow = (flow: "a" | "b"): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(FLOW_STORAGE_KEY, flow);
  } catch {
    // Ignore storage write failures (private mode, disabled storage, etc.).
  }
};

const App = () => {
  const posthog = usePostHog();
  const flow = useMemo(() => {
    const { flow: resolvedFlow, shouldPersistFlow } = resolveInitialFlowState(
      window.location.href,
      readStoredFlow()
    );
    if (shouldPersistFlow) {
      writeStoredFlow(resolvedFlow);
    }
    return resolvedFlow;
  }, []);
  const steps = useMemo(() => getFlowSteps(flow), [flow]);
  const funnelSections = useMemo(() => getFunnelSections(steps), [steps]);
  const initialStepId = useMemo(() => resolveStepFromUrl(), []);
  const {
    answers,
    currentIndex,
    currentStep,
    direction,
    canGoBack,
    canContinue,
    goBack,
    goNext,
    goToIndex,
    setSingleAnswer,
    toggleMultiAnswer,
    setTextAnswer
  } = useFunnelState(steps, { flowKey: flow, initialStepId });
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const { plans: livePlans } = useLivePrices();
  const [, setIsWelcomeHeroReady] = useState(true);
  const emailSubmittedRef = useRef(false);
  const leadIdRef = useRef<number | null>(null);
  const backgroundWarmupStartedRef = useRef(false);
  const shouldAnimateOnEntryRef = useRef(false);
  const isWelcomeStep = currentStep?.id === "welcome";

  useEffect(() => {
    shouldAnimateOnEntryRef.current = true;
  }, []);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("body-welcome-step", isWelcomeStep);

    return () => {
      document.body.classList.remove("body-welcome-step");
    };
  }, [isWelcomeStep]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const welcomeStep = steps[0];
      if (welcomeStep?.kind === "welcome") {
        startPhasedImagePreloadOnce({
          runKey: `flow:${flow}:welcome-hero`,
          critical: getImagePreloadCandidatesForSources(
            welcomeStep.heroSlides?.map((slide) => slide.image)
          ),
          secondary: [],
          concurrency: 1
        });

        const validationWarnings = buildFunnelImageRepository({
          steps,
          currentIndex: 0,
          lookaheadSteps: 0
        }).warnings;

        if (import.meta.env.DEV && validationWarnings.length > 0) {
          console.warn("[image-preload] validation warnings", validationWarnings);
        }
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [flow, steps]);

  useEffect(() => {
    if (!currentStep || currentStep.kind === "welcome") {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const imageRepository = buildFunnelImageRepository({
        steps,
        currentIndex,
        lookaheadSteps: 1
      });

      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:step:${currentStep.id}`,
        critical: imageRepository.critical,
        secondary: imageRepository.secondary,
        concurrency: 2
      });

      if (import.meta.env.DEV && imageRepository.warnings.length > 0) {
        console.warn("[image-preload] validation warnings", imageRepository.warnings);
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, currentStep, flow, steps]);

  const checkoutPreloadTriggerIndex = useMemo(() => {
    const paymentStepIndex = steps.findIndex((step) => step.kind === "payment");
    if (paymentStepIndex < 0) {
      return -1;
    }
    return Math.max(paymentStepIndex - 2, 0);
  }, [steps]);

  useEffect(() => {
    if (checkoutPreloadTriggerIndex < 0 || currentIndex < checkoutPreloadTriggerIndex) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:checkout-images`,
        critical: getCheckoutImagePreloadUrls(),
        secondary: []
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [checkoutPreloadTriggerIndex, currentIndex, flow]);

  useEffect(() => {
    const startupWindow = window as WindowWithStartupFlags;
    if (startupWindow.__VIREO_WELCOME_SHELL_CAROUSEL_TIMER__) {
      window.clearInterval(startupWindow.__VIREO_WELCOME_SHELL_CAROUSEL_TIMER__);
      startupWindow.__VIREO_WELCOME_SHELL_CAROUSEL_TIMER__ = undefined;
    }

    document.documentElement.dataset.appReady = "true";
  }, []);

  useEffect(() => {
    if (!currentStep || currentStep.id !== "welcome") {
      return;
    }

    const startupWindow = window as WindowWithStartupFlags;
    if (!startupWindow.__VIREO_WELCOME_SHELL_CONTINUE__) {
      return;
    }

    startupWindow.__VIREO_WELCOME_SHELL_CONTINUE__ = false;
    const continueTimer = window.setTimeout(() => {
      goNext();
    }, 0);

    return () => {
      window.clearTimeout(continueTimer);
    };
  }, [currentStep, goNext]);

  useEffect(() => {
    const startupWindow = window as WindowWithStartupFlags;
    const pendingAnswer = startupWindow.__VIREO_WELCOME_SHELL_PENDING_ANSWER__;
    if (!pendingAnswer) {
      return;
    }

    startupWindow.__VIREO_WELCOME_SHELL_PENDING_ANSWER__ = undefined;
    setSingleAnswer(pendingAnswer.answerKey, pendingAnswer.value);
  }, [setSingleAnswer]);

  useEffect(() => {
    if (!currentStep || currentStep.id !== "welcome" || backgroundWarmupStartedRef.current) {
      return;
    }

    backgroundWarmupStartedRef.current = true;

    const warmupStartIndex = Math.min(currentIndex + 1, steps.length - 1);
    const postWelcomeImageRepository = buildFunnelImageRepository({
      steps,
      currentIndex: warmupStartIndex,
      lookaheadSteps: 1
    });
    let secondWarmupTask: ScheduledTaskHandle | null = null;

    const firstWarmupTask = scheduleIdleTask(() => {
      void preloadStepRendererChunk(steps[currentIndex + 1]?.kind);

      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:next-step-images`,
        critical: postWelcomeImageRepository.critical,
        secondary: [],
        concurrency: 1
      });

      secondWarmupTask = scheduleIdleTask(() => {
        const stepKinds = Array.from(new Set(steps.slice(currentIndex + 1).map((step) => step.kind)));

        void Promise.all([
          preloadLateStepModules(),
          ...stepKinds.map((kind) => preloadStepRendererChunk(kind))
        ]);

        startPhasedImagePreloadOnce({
          runKey: `flow:${flow}:background-secondary`,
          critical: [],
          secondary: postWelcomeImageRepository.secondary,
          concurrency: 2
        });
      }, 2500);
    }, 1200);

    return () => {
      cancelScheduledTask(firstWarmupTask);
      cancelScheduledTask(secondWarmupTask);
    };
  }, [currentIndex, currentStep, flow, steps]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    if (currentStep.kind !== "welcome") {
      setIsWelcomeHeroReady(true);
      return;
    }

    setIsWelcomeHeroReady(!(currentStep.heroImage || currentStep.heroVideo));
  }, [currentStep]);

  useEffect(() => {
    if (currentStep?.kind === "welcome") {
      return;
    }

    const nextStep = steps[currentIndex + 1];
    if (!nextStep) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      void preloadStepRendererChunk(nextStep.kind);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, currentStep, steps]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    replaceStepInUrl(currentStep.id);

    const currentUrl = window.location.href;
    const trackingWindow = window as WindowWithStartupFlags;
    if (trackingWindow.__VIREO_LAST_PAGEVIEW_URL__ === currentUrl) {
      return;
    }

    trackingWindow.__VIREO_LAST_PAGEVIEW_URL__ = currentUrl;
    posthog.capture("$pageview", {
      $current_url: currentUrl,
      flow_variant: flow,
      funnel_flow: flow,
      funnel_hash_route: window.location.hash || `#${currentStep.id}`,
      funnel_step_id: currentStep.id,
      funnel_step_index: currentIndex
    });
  }, [currentIndex, currentStep, flow, posthog]);

  useEffect(() => {
    const onBrowserNavigation = () => {
      const stepIdFromUrl = resolveStepFromUrl();
      if (!stepIdFromUrl) {
        return;
      }

      const nextIndex = steps.findIndex((step) => step.id === stepIdFromUrl);
      if (nextIndex >= 0) {
        goToIndex(nextIndex);
      }
    };

    window.addEventListener("popstate", onBrowserNavigation);
    window.addEventListener("hashchange", onBrowserNavigation);
    return () => {
      window.removeEventListener("popstate", onBrowserNavigation);
      window.removeEventListener("hashchange", onBrowserNavigation);
    };
  }, [goToIndex, steps]);

  if (!currentStep) {
    return null;
  }

  const resolvedCurrentStep = currentStep.kind === "payment"
    ? { ...currentStep, plans: livePlans }
    : currentStep;

  const sectionProgress = computeSectionProgress(currentIndex, funnelSections);
  const isPaymentStep = resolvedCurrentStep.kind === "payment";
  const isLoadingStep = resolvedCurrentStep.kind === "loading";
  /** Welcome + loading stay locked; other steps need scroll so question options are not clipped below tall hero images. Exception: welcome with embedded question needs to scroll to show all options. */
  const isFunnelContentScrollable =
    (resolvedCurrentStep.kind !== "welcome" && resolvedCurrentStep.kind !== "loading") ||
    (resolvedCurrentStep.kind === "welcome" && !!resolvedCurrentStep.embeddedQuestion);
  const isCheckoutCompleteStep = resolvedCurrentStep.id === "checkout_complete";
  const shouldShowBottomBar =
    !isLoadingStep &&
    !isPaymentStep &&
    !isCheckoutCompleteStep &&
    !(isWelcomeStep && resolvedCurrentStep.kind === "welcome" && !!resolvedCurrentStep.embeddedQuestion);
  const confirmLabelText = resolvedCurrentStep.confirmLabel ?? "Continue →";
  const welcomeFinePrint = resolvedCurrentStep.kind === "welcome" ? resolvedCurrentStep.finePrint : undefined;
  const confirmLabel = confirmLabelText;
  const confirmDisabled = !canContinue || isStartingCheckout;
  const disabledHint =
    resolvedCurrentStep.kind === "question" && resolvedCurrentStep.selection === "single"
      ? "Pick an option to continue"
      : undefined;

  const centerAlignedQuestionIds = ["cooking_goals", "your_why", "biggest_blocker"];
  const isCenterableQuestionPage =
    resolvedCurrentStep.kind === "question" &&
    ((resolvedCurrentStep.selection === "single" &&
      resolvedCurrentStep.options.length <= 5 &&
      !resolvedCurrentStep.optionFeedback &&
      !resolvedCurrentStep.helperText) ||
      centerAlignedQuestionIds.includes(resolvedCurrentStep.id));

  const handleConfirm = async () => {
    if (!canContinue || isStartingCheckout) {
      return;
    }

    if (resolvedCurrentStep.kind === "welcome") {
      const { trackMetaViewContent } = await import("./analytics/metaPixel");
      trackMetaViewContent();
    }

    if (resolvedCurrentStep.kind === "payment") {
      const selectedPlanId = answers[resolvedCurrentStep.answerKey];
      if (typeof selectedPlanId !== "string") {
        return;
      }

      const selectedPlan = resolvedCurrentStep.plans.find((plan) => plan.id === selectedPlanId);
      if (!selectedPlan) {
        return;
      }

      const [
        { createCheckoutSession },
        { getMetaTrackingContext, rememberPendingCheckoutTracking }
      ] = await Promise.all([
        import("./api/checkout"),
        import("./analytics/metaPixel")
      ]);
      const trackingContext = getMetaTrackingContext();

      const emailStepEnabled = steps.some(
        (step) => step.kind === "email" && step.answerKey === "email"
      );
      const email = emailStepEnabled && typeof answers.email === "string" ? answers.email : "";
      const planValue = selectedPlan.amount ?? (parseFloat(selectedPlan.totalPrice.replace(/[^0-9.]/g, "")) || 0);
      const planCurrency = selectedPlan.currencyCode ?? "USD";
      const checkoutEventProps = {
        funnel_flow: flow,
        funnel_step_id: resolvedCurrentStep.id,
        funnel_step_index: currentIndex,
        checkout_plan_id: selectedPlan.id,
        checkout_plan_value: planValue,
        checkout_currency: planCurrency,
        has_email: Boolean(email),
        has_lead_id: leadIdRef.current !== null
      };

      setIsStartingCheckout(true);
      try {
        try {
          posthog.capture("checkout_started", checkoutEventProps);
        } catch (trackingError) {
          console.warn("[posthog] checkout_started not captured", trackingError);
        }

        const session = await createCheckoutSession({
          ...(email ? { email } : {}),
          planId: selectedPlan.id,
          flow,
          currency: planCurrency,
          value: planValue,
          eventId: trackingContext.eventId,
          ...(leadIdRef.current ? { leadId: leadIdRef.current } : {}),
          ...(trackingContext.fbp ? { fbp: trackingContext.fbp } : {}),
          ...(trackingContext.fbc ? { fbc: trackingContext.fbc } : {})
        });

        logMetaDedup("checkout_session_created", {
          source: "browser",
          sessionId: session.sessionId,
          eventId: trackingContext.eventId,
          planId: selectedPlan.id,
          currency: planCurrency,
          value: planValue
        });

        // Persist flow before redirect so Stripe return URLs can recover attribution.
        writeStoredFlow(flow);

        rememberPendingCheckoutTracking({
          sessionId: session.sessionId,
          eventId: trackingContext.eventId,
          planId: selectedPlan.id,
          value: planValue,
          currency: planCurrency,
          completionToken: session.completionToken
        });

        try {
          posthog.capture("checkout_redirected_to_stripe", {
            ...checkoutEventProps,
            checkout_session_id: session.sessionId
          });
        } catch (trackingError) {
          console.warn("[posthog] checkout_redirected_to_stripe not captured", trackingError);
        }
        window.location.assign(session.checkoutUrl);
      } catch (error) {
        try {
          posthog.capture("checkout_session_failed", {
            ...checkoutEventProps,
            error_message: error instanceof Error ? error.message : String(error)
          });
        } catch (trackingError) {
          console.warn("[posthog] checkout_session_failed not captured", trackingError);
        }
        console.error("[checkout] failed to create Stripe checkout session", error);
      } finally {
        setIsStartingCheckout(false);
      }

      return;
    }

    if (resolvedCurrentStep.kind === "email") {
      if (emailSubmittedRef.current) {
        goNext();
        return;
      }
      const email = answers[resolvedCurrentStep.answerKey] as string;
      const [
        { buildOnboardingResponses },
        { saveLeadWithRetry, trackLeadServerEvent },
        { getMetaTrackingContext, trackMetaLead }
      ] = await Promise.all([
        import("./funnel/buildOnboardingResponses"),
        import("./api/leads"),
        import("./analytics/metaPixel")
      ]);
      const onboardingResponses = buildOnboardingResponses(steps, answers);
      const leadTrackingContext = getMetaTrackingContext();
      const leadEventId = `lead-${leadTrackingContext.eventId}`;
      emailSubmittedRef.current = true;
      goNext();
      void (async () => {
        const leadTrackPromise = trackLeadServerEvent({
          email,
          eventId: leadEventId,
          fbp: leadTrackingContext.fbp,
          fbc: leadTrackingContext.fbc
        });
        const [leadSaveResult, leadTrackResult] = await Promise.allSettled([
          saveLeadWithRetry(email, onboardingResponses),
          leadTrackPromise
        ]);

        if (leadSaveResult.status === "fulfilled") {
          leadIdRef.current = leadSaveResult.value;
        } else {
          console.warn("[leads] failed to save lead after retries", leadSaveResult.reason);
        }

        if (leadTrackResult.status === "rejected") {
          console.warn("[leads] server-side lead tracking failed", leadTrackResult.reason);
        }

        trackMetaLead(leadEventId);
      })();
      return;
    }

    goNext();
  };

  const handleSelectSingle = (answerKey: string, optionId: string) => {
    setSingleAnswer(answerKey, optionId);
    const shouldAutoAdvance =
      currentStep.kind === "question" && !currentStep.optionFeedback;
    if (shouldAutoAdvance) {
      setTimeout(goNext, 100);
    }
  };

  const handleWelcomeEmbeddedSelect = (answerKey: string, optionId: string) => {
    setSingleAnswer(answerKey, optionId);

    // Flow B asks this inside the welcome step, so emit a synthetic funnel step pageview.
    if (answerKey === "cooking_for") {
      try {
        posthog.capture(
          "$pageview",
          buildEmbeddedStepPageviewPayload(
            window.location.href,
            flow,
            "cooking_for",
            currentIndex + 1
          )
        );
      } catch (trackingError) {
        console.warn("[posthog] embedded cooking_for pageview not captured", trackingError);
      }
    }

    window.setTimeout(() => {
      goNext();
    }, 100);
  };

  return (
    <div className={`app-frame ${isWelcomeStep ? "app-frame-welcome" : ""}`}>
      <FunnelShell
        isScrollable={isFunnelContentScrollable}
        topBar={
          isPaymentStep ? null : (
            <TopBar
              canGoBack={canGoBack && currentStep.id !== "checkout_complete"}
              onBack={goBack}
              sections={funnelSections}
              sectionProgress={sectionProgress}
              isWelcomeStep={isWelcomeStep}
              useWelcomeChrome
            />
          )
        }
        bottomBar={
          shouldShowBottomBar ? (
            <BottomConfirmBar
              label={confirmLabel}
              disabled={confirmDisabled}
              disabledHint={disabledHint}
              onClick={handleConfirm}
              note={welcomeFinePrint}
              className={isWelcomeStep ? "bottom-confirm-bar-welcome" : undefined}
            />
          ) : undefined
        }
      >
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={currentStep.id}
            className={`step-page ${isWelcomeStep ? "step-page-welcome" : ""} ${
              isCenterableQuestionPage ? "step-page-question-centered" : ""
            }`.trim()}
            custom={direction}
            variants={pageVariants}
            initial={shouldAnimateOnEntryRef.current ? "enter" : false}
            animate="center"
            exit="exit"
            transition={pageTransition}
          >
            <StepRenderer
              step={resolvedCurrentStep}
              answers={answers}
              onSelectSingle={handleSelectSingle}
              onToggleMulti={toggleMultiAnswer}
              onSetText={setTextAnswer}
              onWelcomeFirstImageReadyChange={setIsWelcomeHeroReady}
              onWelcomeEmbeddedSelect={handleWelcomeEmbeddedSelect}
              onLoadingContinue={goNext}
              onPaymentContinue={handleConfirm}
              isPaymentSubmitting={isStartingCheckout}
            />
          </motion.div>
        </AnimatePresence>
      </FunnelShell>
    </div>
  );
};

export default App;
